import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Stack, Switch, Typography } from '@mui/material';
import { Campaign, CardGiftcard, Group, Inventory2 } from '@mui/icons-material';
import { colors } from '../theme/theme';

type Key = 'benefits' | 'referrals' | 'announcements';

interface Props {
  sharing: Partial<Record<Key | 'packs', boolean>> | undefined;
  locationCount: number;
  /** Only the flags that changed; the backend sets each one on its own. */
  onSave: (changes: Partial<Record<Key, boolean>>) => Promise<void>;
  onError: (message: string) => void;
}

/**
 * Same palette idea as the Access tab: one soft colour per row, used on its
 * icon tile. All of these are MEMBER-facing — staff reach is the Access tab.
 */
const ITEMS: { key: Key; label: string; help: string; icon: React.ReactNode; bg: string; fg: string }[] = [
  {
    key: 'benefits', label: 'Rewards', help: 'A reward or credit earned at one location can be spent at the others.',
    icon: <CardGiftcard fontSize="small" />, bg: '#fdeef4', fg: colors.primaryDark,
  },
  {
    key: 'referrals', label: 'Referral programme', help: 'Members get one code, wherever they joined.',
    icon: <Group fontSize="small" />, bg: '#f3e5f5', fg: '#7b1fa2',
  },
  {
    key: 'announcements', label: 'Announcements', help: 'An announcement posted at one location shows at the others.',
    icon: <Campaign fontSize="small" />, bg: '#e3effc', fg: '#1d5fa8',
  },
];

const Tile: React.FC<{ bg: string; fg: string; children: React.ReactNode }> = ({ bg, fg, children }) => (
  <Box sx={{
    width: 38, height: 38, borderRadius: 2, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: bg, color: fg,
  }}>
    {children}
  </Box>
);

const BrandSharingTab: React.FC<Props> = ({ sharing, locationCount, onSave, onError }) => {
  const initial = useMemo(
    () => Object.fromEntries(ITEMS.map((i) => [i.key, !!sharing?.[i.key]])) as Record<Key, boolean>,
    [sharing],
  );
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial); }, [initial]);

  const changes = useMemo(() => {
    const out: Partial<Record<Key, boolean>> = {};
    ITEMS.forEach(({ key }) => { if (form[key] !== initial[key]) out[key] = form[key]; });
    return out;
  }, [form, initial]);
  const changeCount = Object.keys(changes).length;

  const save = async () => {
    if (!changeCount) return;
    setSaving(true);
    try {
      await onSave(changes);
    } catch (e: any) {
      onError(e?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const rowSx = {
    display: 'flex', alignItems: 'center', gap: 2, py: 2,
    '&:not(:last-child)': { borderBottom: `1px solid ${colors.divider}` },
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>Sharing</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What members can use across locations.
      </Typography>

      {locationCount <= 1 && (
        <Box sx={{
          mb: 2, px: 2, py: 1.25, borderRadius: 2, fontSize: 13,
          bgcolor: '#e3effc', color: '#1d5fa8',
        }}>
          One location so far — these take effect once a second is added.
        </Box>
      )}

      <Box sx={{
        bgcolor: colors.backgroundTertiary, border: `1px solid ${colors.divider}`,
        borderRadius: 3, px: { xs: 2, sm: 3 },
      }}>
        {ITEMS.map((it) => (
          <Box key={it.key} sx={rowSx}>
            <Tile bg={it.bg} fg={it.fg}>{it.icon}</Tile>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>{it.label}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                {it.help}
              </Typography>
            </Box>
            <Switch
              checked={form[it.key]}
              onChange={(e) => setForm({ ...form, [it.key]: e.target.checked })}
            />
          </Box>
        ))}

        {/* Packs are shared PER PACK (the pack form's "Usable at every
            location"), so there is no brand-wide switch — just say where. */}
        <Box sx={rowSx}>
          <Tile bg="#e6f4e7" fg="#2e7d32"><Inventory2 fontSize="small" /></Tile>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600}>Packs</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
              Turn on <b>Usable at every location</b> inside each pack.
            </Typography>
          </Box>
          <Chip size="small" label="Per pack" sx={{ bgcolor: '#e6f4e7', color: '#2e7d32', fontWeight: 600 }} />
        </Box>
      </Box>

      {/* Save bar — same as Settings. */}
      <Box sx={{
        position: 'sticky', bottom: 0, zIndex: 5, mt: 3, mx: { xs: -2, md: 0 }, px: { xs: 2, md: 0 }, py: 2,
        bgcolor: colors.white, borderTop: `1px solid ${changeCount ? colors.primaryLight : colors.divider}`,
      }}>
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
            {changeCount ? `${changeCount} unsaved change${changeCount === 1 ? '' : 's'}` : 'All changes saved'}
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ '& > *': { flex: { xs: 1, sm: 'none' } } }}>
            <Button
              onClick={() => setForm(initial)}
              disabled={!changeCount || saving}
              sx={{
                borderRadius: 999, px: 4, color: colors.primaryDark,
                bgcolor: `${colors.primary}26`, '&:hover': { bgcolor: `${colors.primary}40` },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={save}
              disabled={!changeCount || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ borderRadius: 999, px: 4 }}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default BrandSharingTab;
